/**
 * Monetization type definitions — shared between server and client.
 * DO NOT EDIT in server/src/shared/common/ or phaser/src/shared/common/
 * Edit the canonical copy in shared/ and run: node scripts/sync-shared.js
 */

// ============ ENUMS ============

export type Chain = 'solana' | 'ethereum' | 'polygon' | 'bsc' | 'galachain' | 'galaswap';
export type Stablecoin = 'USDC' | 'USDT';
export type AssetType = 'ship' | 'component' | 'station' | 'alliance_hq' | 'crew_slot' | 'orbital_platform' | 'isd' | 'test';
export type PurchaseStatus = 'pending' | 'active' | 'refunded' | 'retired';
export type RefundStatus = 'pending' | 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
export type StationType = 'waypoint' | 'depot' | 'fortress' | 'capital';
export type StationStatus = 'inventory' | 'deploying' | 'active' | 'relocating' | 'destroyed' | 'retired';
export type PvPZoneType = 'safe_zone' | 'station_raid_enabled' | 'full_pvp';
export type InsuranceStatus = 'active' | 'lapsed' | 'voided' | 'claimed';
export type AllianceHQTier = 'outpost' | 'station' | 'fortress' | 'citadel';
export type LPDistributionMode = 'equal_split' | 'rank_weighted' | 'activity_weighted' | 'custom';
export type GoldenAsteroidVariant = 'common_gold' | 'rare_platinum' | 'legendary_dark_matter' | 'special_crypto';
export type WithdrawalStatus = 'pending_email_confirmation' | 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
export type PayoutWithdrawalStatus = 'unclaimed' | 'claimed' | 'withdrawn';
export type CrewStatus = 'barracks' | 'assigned' | 'in_combat' | 'recovering';
export type ComponentCategory = 'weapon' | 'engine' | 'shield' | 'armor' | 'sensor' | 'stealth' | 'reactor' | 'special';
export type ComponentTier = 'mk1' | 'mk2' | 'mk3' | 'mk4' | 'prototype';
export type RaidOutcome = 'attacker_wins' | 'defender_wins' | 'draw' | 'aborted';

// ============ PURCHASE SYSTEM ============

export interface Purchase {
  id: string;
  userId: string;
  assetType: AssetType;
  assetId: string;
  chain: Chain;
  stablecoin: Stablecoin;
  amountUsd: number;
  amountToLp: number;
  amountToTokenBuy: number;
  lpTokenAmount: number;
  lpPoolId: string | null;
  isdBought: number;
  txHash: string | null;
  walletAddress: string | null;
  verifiedAt: string | null;
  status: PurchaseStatus;
  purchasedAt: string;
  refundedAt: string | null;
}

export interface Refund {
  id: string;
  userId: string;
  purchaseId: string;
  shipId: string | null;
  componentsRetired: string[];
  totalInvested: number;
  lpTokensRedeemed: number;
  stablecoinReceived: number;
  isdSold: number;
  totalRedeemedUsd: number;
  amountToPlayer: number;
  amountToTreasury: number;
  exitFeeCharged: number;
  netToPlayer: number;
  chain: Chain;
  status: RefundStatus;
  requestedAt: string;
  processedAt: string | null;
  queuePosition: number | null;
}

// ============ LP POOLS ============

export interface LPPool {
  id: string;
  chain: Chain;
  pair: string;
  dex: string;
  totalValueLocked: number;
  totalFeesEarned: number;
  feesToGoldenAsteroids: number;
  feesToTreasury: number;
  feesToAllianceSharing: number;
  impermanentLossPct: number;
  poolAddress: string | null;
  isActive: boolean;
  lastSyncedAt: string;
}

export interface LPPosition {
  id: string;
  purchaseId: string;
  userId: string;
  lpPoolId: string;
  lpTokenAmount: number;
  initialValueUsd: number;
  currentValueUsd: number;
  onchainPositionId: string | null;
  tokenAAmount: number;
  tokenBAmount: number;
  feeTier: number;
  uncollectedFees: number;
  status: 'active' | 'redeemed';
  createdAt: string;
  redeemedAt: string | null;
}

export interface LPPositionDetail {
  id: string;
  lpPositionId: string;
  onchainPositionId: string | null;
  tokenASymbol: string;
  tokenBSymbol: string;
  tokenAAmount: number;
  tokenBAmount: number;
  feeTier: number;
  tickLower: number | null;
  tickUpper: number | null;
  uncollectedFeesA: number;
  uncollectedFeesB: number;
  lastCollectedAt: string | null;
}

export interface TreasuryFeeCollection {
  id: string;
  lpPoolId: string;
  lpPositionId: string | null;
  feesCollectedA: number;
  feesCollectedB: number;
  feesCollectedUsd: number;
  toGoldenAsteroids: number;
  toTreasury: number;
  toAllianceSharing: number;
  collectionMethod: 'scheduled' | 'manual';
  txHash: string | null;
  collectedAt: string;
}

export interface UserEarnings {
  userId: string;
  totalEarnedUsd: number;
  totalEarnedIsd: number;
  goldenAsteroidUsd: number;
  auctionHouseUsd: number;
  allianceLpUsd: number;
  stationFeesUsd: number;
  lastCalculatedAt: string;
}

export interface TokenRegistryEntry {
  symbol: string;
  chain: string;
  chainSymbol: string;
  contractAddress: string;
  decimals: number;
  isActive: boolean;
}

// ============ GOLDEN ASTEROID REWARDS ============

export interface RewardPool {
  balanceUsd: number;
  totalDeposited: number;
  totalPaidOut: number;
  rewardCommonGold: number;
  rewardRarePlatinum: number;
  rewardLegendaryDarkMatter: number;
  isActive: boolean;
}

export interface GoldenAsteroidPayout {
  id: string;
  userId: string;
  worldId: number;
  asteroidId: string;
  variant: GoldenAsteroidVariant;
  rewardUsd: number;
  rewardUsdValue: number | null;
  stationId: string | null;
  stationOwnerId: string | null;
  stationFeePct: number | null;
  stationFeeUsd: number | null;
  finderPayoutUsd: number | null;
  status: 'queued' | 'paid' | 'failed';
  paidAt: string;
}

// ============ REWARD WITHDRAWALS ============

export interface RewardWithdrawal {
  id: string;
  userId: string;
  requestId: string;
  walletAddress: string;
  chain: Chain;
  grossAmountUsd: number;
  exitFeePct: number;
  exitFeeUsd: number;
  netAmountUsd: number;
  tokenName: string | null;
  grossTokenAmount: number | null;
  payoutIds: string[];
  txHash: string | null;
  status: WithdrawalStatus;
  queuePosition: number | null;
  retryCount: number;
  maxRetries: number;
  errorMessage: string | null;
  requestedAt: string;
  processingStartedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
}

// ============ CREW ROLES SYSTEM ============

export type CrewRoleType = 'captain' | 'navigator' | 'combat_tech' | 'maintenance' | 'reactor_chief' | 'special_tech';
export type CrewStatName = 'strength' | 'intelligence' | 'health' | 'wisdom' | 'luck';

export interface CrewRole {
  id: string;
  userId: string;
  role: CrewRoleType;
  name: string;
  level: number;
  currentXp: number;
  xpToNextLevel: number;
  unspentPoints: number;
  statStrength: number;
  statIntelligence: number;
  statHealth: number;
  statWisdom: number;
  statLuck: number;
}

export interface CrewRolesData {
  roles: CrewRole[];
  levelUpCount: number;
}

// ============ CREW SYSTEM ============

export interface Crew {
  id: string;
  userId: string;
  name: string;
  level: number;
  totalXp: number;
  combatSkill: number;
  pilotingSkill: number;
  navigationSkill: number;
  engineeringSkill: number;
  scienceSkill: number;
  scoutProficiency: number;
  fighterProficiency: number;
  destroyerProficiency: number;
  bomberProficiency: number;
  cruiserProficiency: number;
  carrierProficiency: number;
  battleshipProficiency: number;
  dreadnoughtProficiency: number;
  titanProficiency: number;
  spaceTugProficiency: number;
  assignedShipId: string | null;
  slotId: string;
  status: CrewStatus;
}

export interface CrewSlot {
  id: string;
  userId: string;
  slotNumber: number;
  purchaseId: string | null;
  isDefault: boolean;
}

export interface CrewBonuses {
  attackBonus: number;
  defenseBonus: number;
  speedBonus: number;
  evasionBonus: number;
  sensorBonus: number;
  repairBonus: number;
}

// ============ SPACE STATIONS ============

export interface Station {
  id: string;
  userId: string;
  worldId: number | null;
  stationType: StationType;
  positionQ: number | null;
  positionR: number | null;
  currentHp: number;
  maxHp: number;
  storedAsteroids: number;
  maxStorage: number;
  stationFeePct: number;
  status: StationStatus;
  purchaseId: string | null;
  placedAt: string | null;
  hasShipyard: boolean;
}

export interface StationAsteroid {
  id: string;
  stationId: string;
  asteroidId: string;
  variant: GoldenAsteroidVariant;
  finderUserId: string;
  rewardUsd: number;
  depositedAt: string;
  status: 'stored' | 'retrieved' | 'raided' | 'delivered';
}

// ============ PVP ZONES ============

export interface PvPZone {
  id: string;
  worldId: number;
  zoneType: PvPZoneType;
  hexCenterQ: number;
  hexCenterR: number;
  radius: number;
  name: string;
  description: string;
  spawnRateMultiplier: number;
  mineralRichnessMultiplier: number;
  dangerMultiplier: number;
  loreDescription: string;
  isActive: boolean;
}

// ============ STATION INSURANCE ============

export interface StationInsurance {
  id: string;
  stationId: string;
  userId: string;
  status: InsuranceStatus;
  basePremiumRatePct: number;
  currentMultiplier: number;
  effectiveRatePct: number;
  lastPremiumPaid: string | null;
  lastDefenseCheck: string | null;
  ownerLastOnline: string;
  coverageExpiresAt: string | null;
  totalPremiumsPaid: number;
  claims: number;
}

// ============ STATION RAIDS ============

export interface StationRaid {
  id: string;
  stationId: string;
  attackerUserId: string;
  defenderUserId: string;
  worldId: number;
  asteroidsStolen: number;
  asteroidsValueUsd: number;
  outcome: RaidOutcome;
  insuranceClaimed: boolean;
  insurancePayoutUsd: number;
  raidedAt: string;
}

// ============ ALLIANCE ECONOMY ============

export interface AllianceHQ {
  id: string;
  allianceId: number;
  hqTier: AllianceHQTier;
  memberCap: number;
  lpShareEnabled: boolean;
  lpSharePct: number;
  lpShareMaxPct: number;
  distributionMode: LPDistributionMode;
  purchaseId: string | null;
  purchasedByUserId: string;
  status: 'active' | 'retired';
}

export interface AllianceLPDistribution {
  id: string;
  allianceId: number;
  hqId: string;
  periodStart: string;
  periodEnd: string;
  totalLpFeesIsd: number;
  sharePctApplied: number;
  totalDistributedIsd: number;
  distributionMode: LPDistributionMode;
  memberPayouts: Array<{ userId: string; amount: number; share: number }>;
  memberCount: number;
  distributedAt: string;
}

// ============ COMPONENT MARKETPLACE ============

export interface ComponentDefinition {
  id: string;
  name: string;
  category: ComponentCategory;
  tier: ComponentTier;
  description: string;
  stats: Record<string, number>;
  priceUsd: number;
  upgradeFromId: string | null;
  upgradePriceUsd: number | null;
  compatibleShipTypes: string[];
  isAvailable: boolean;
  sortOrder: number;
}

export interface ComponentInstance {
  id: string;
  definitionId: string;
  userId: string;
  shipId: string | null;
  purchaseId: string | null;
  totalInvestedUsd: number;
  currentTier: ComponentTier;
  status: 'equipped' | 'retired';
  starRating: number;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  enhancementPityCount: number;
  conditionPercent?: number;
}

// ============ COMPONENT ENHANCEMENT ============

export type ComponentSkillLevel = 1 | 2 | 3 | 4 | 5 | 6;

export interface CrewComponentSkill {
  crewId: string;
  componentType: string;
  skillLevel: ComponentSkillLevel;
  currentXp: number;
  totalXpEarned: number;
  skillName: string;
  bonusPct: number;
}

export interface EnhancementResult {
  success: boolean;
  newStarRating?: number;
  newRarity?: string;
  pityTriggered?: boolean;
  isdSpent: number;
  error?: string;
}

export interface ComponentWithEnhancements {
  instanceId: string;
  definitionId: string;
  name: string;
  category: string;
  baseStats: Record<string, number>;
  starRating: number;
  rarity: string;
  starMultiplier: number;
  crewSkillBonus: number;
  effectiveStats: Record<string, number>;
}

export interface EnhancementInfo {
  starRating: number;
  rarity: string;
  pityCount: number;
  nextCost: number | null;
  successRate: number | null;
  pityThreshold: number | null;
  starMultiplier: number;
}

// ============ EXIT FEES ============

export interface ExitFeeCollection {
  id: string;
  userId: string;
  transactionType: 'refund' | 'withdrawal' | 'marketplace_sale' | 'reward_cashout';
  grossAmount: number;
  feePct: number;
  feeAmount: number;
  netAmount: number;
  referenceId: string | null;
  chain: Chain;
  collectedAt: string;
}

// ============ SYSTEM CONFIG ============

export interface SystemConfigEntry {
  key: string;
  value: unknown;
  description: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface LPFeeSplit {
  golden_asteroids_pct: number;
  treasury_pct: number;
  alliance_sharing_pct: number;
}

// ============ SHIP PRICING ============

export interface ShipPricing {
  unitType: string;
  priceUsd: number;
  componentSlots: number;
  tonnage: number;
}

export const SHIP_USD_PRICES: Record<string, ShipPricing> = {
  scout: { unitType: 'scout', priceUsd: 5, componentSlots: 2, tonnage: 10 },
  fighter: { unitType: 'fighter', priceUsd: 25, componentSlots: 3, tonnage: 25 },
  destroyer: { unitType: 'destroyer', priceUsd: 75, componentSlots: 4, tonnage: 40 },
  bomber: { unitType: 'bomber', priceUsd: 150, componentSlots: 3, tonnage: 30 },
  space_tug: { unitType: 'space_tug', priceUsd: 200, componentSlots: 6, tonnage: 15 },
  cruiser: { unitType: 'cruiser', priceUsd: 500, componentSlots: 5, tonnage: 50 },
  carrier: { unitType: 'carrier', priceUsd: 2000, componentSlots: 8, tonnage: 150 },
  battleship: { unitType: 'battleship', priceUsd: 5000, componentSlots: 7, tonnage: 100 },
  dreadnought: { unitType: 'dreadnought', priceUsd: 15000, componentSlots: 10, tonnage: 200 },
  titan: { unitType: 'titan', priceUsd: 50000, componentSlots: 12, tonnage: 300 },
};

// ============ STATION PRICING ============

export const STATION_USD_PRICES: Record<StationType, number> = {
  waypoint: 500,
  depot: 2000,
  fortress: 10000,
  capital: 50000,
};

export const STATION_MAX_STORAGE: Record<StationType, number> = {
  waypoint: 3,
  depot: 10,
  fortress: 25,
  capital: 999,
};

// ============ ALLIANCE HQ PRICING ============

export const ALLIANCE_HQ_USD_PRICES: Record<AllianceHQTier, number> = {
  outpost: 100,
  station: 500,
  fortress: 2000,
  citadel: 10000,
};

export const ALLIANCE_HQ_MEMBER_CAPS: Record<AllianceHQTier, number> = {
  outpost: 10,
  station: 25,
  fortress: 50,
  citadel: 100,
};

export const ALLIANCE_HQ_LP_SHARE_MAX: Record<AllianceHQTier, number> = {
  outpost: 0,
  station: 5,
  fortress: 10,
  citadel: 20,
};

// ============ MINERAL ECONOMY ============

export type MineralRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

export interface MineralType {
  id: string;
  name: string;
  displayName: string;
  rarity: MineralRarity;
  baseValueUsd: number;
  color: string;
  description: string;
}

export interface PlayerMineral {
  mineralTypeId: string;
  mineralName: string;
  displayName: string;
  amount: number;
  totalMined: number;
  totalSpent: number;
  rarity: string;
  color: string;
}

// ============ PER-LOCATION MINERALS ============

export type LocationType = 'station' | 'planet';

export interface LocationMineral {
  locationType: LocationType;
  locationId: string;
  ownerId: string;
  mineralTypeId: string;
  mineralName: string;
  displayName: string;
  amount: number;
  rarity: string;
  color: string;
}

export type MineralTransferStatus = 'active' | 'completed' | 'cancelled';

export interface MineralTransfer {
  id: string;
  batchId: string;
  ownerId: string;
  fromLocationType: LocationType;
  fromLocationId: string;
  toLocationType: LocationType;
  toLocationId: string;
  mineralTypeId: string;
  mineralName: string;
  totalAmount: number;
  transferredAmount: number;
  status: MineralTransferStatus;
  createdAt: string;
  completedAt: string | null;
  cancelledAt: string | null;
}

export interface MineralTransferBatch {
  batchId: string;
  ownerId: string;
  fromLocationType: LocationType;
  fromLocationId: string;
  fromLocationName: string;
  toLocationType: LocationType;
  toLocationId: string;
  toLocationName: string;
  transfers: MineralTransfer[];
  totalMinerals: number;
  transferredMinerals: number;
  estimatedTicksRemaining: number;
  status: MineralTransferStatus;
  createdAt: string;
}

// ============ AUCTION HOUSE ============

export type AuctionListingStatus = 'active' | 'sold' | 'partial' | 'expired' | 'cancelled';

export interface AuctionListing {
  id: string;
  sellerId: string;
  mineralTypeId: string;
  mineralName: string;
  rarity?: string;
  color?: string;
  quantity: number;
  pricePerUnit: number;
  totalPrice: number;
  remainingQuantity: number;
  status: AuctionListingStatus;
  listedAt: string;
  expiresAt: string;
}

export interface AuctionTransaction {
  id: string;
  listingId: string;
  buyerId: string;
  sellerId: string;
  mineralName: string;
  quantity: number;
  pricePerUnit: number;
  totalPrice: number;
  feeIsd: number;
  completedAt: string;
}

export interface MarketSummary {
  mineralTypeId: string;
  mineralName: string;
  lowestPrice: number;
  highestPrice: number;
  totalListings: number;
  totalVolume: number;
  recentAvgPrice: number;
}

// ============ WRECKAGE & REPAIR ============

export type TowDestination = 'pending' | 'earth' | 'station';
export type TowStatus = 'awaiting_decision' | 'awaiting_payment' | 'towing' | 'arrived' | 'expired' | 'declined';
export type RepairStatus = 'not_started' | 'repairing' | 'completed';

export interface UnitWreck {
  id: string;
  originalUnitId: string;
  ownerId: string;
  worldId: number;
  unitType: string;
  shipVariant?: string | null;
  positionQ: number;
  positionR: number;
  maxHp: number;
  maxShields: number;
  towDestination: TowDestination;
  towDestinationId: string | null;
  towCostUsd: number;
  towCostIsd: number;
  towPaymentCurrency: string;
  towPaymentTx: string | null;
  towPaymentVerified: boolean;
  towStatus: TowStatus;
  repairCostMinerals: Record<string, number>;
  repairStatus: RepairStatus;
  repairStartedAt?: string | null;
  destroyedAt: string;
  expiresAt: string;
  destroyedByUserId?: string;
  destroyedByName?: string;
}

export interface TowOption {
  destination: 'earth' | 'station';
  destinationName: string;
  destinationId?: string;
  distance: number;
  costUsd: number;
  costIsd: number;
  estimatedTicks: number;
}

// ============ MOVING ASTEROIDS ============

export interface MovingAsteroidConfig {
  asteroidType?: string;
  variant?: string;
  resourceRichness?: number;
  mineralComposition?: Record<string, number>;
  entryEdge?: 'north' | 'south' | 'east' | 'west';
  speed?: number;
}

// ============ PURSUIT MECHANICS ============

export type FollowMode = 'follow' | 'attack';

export interface FollowStatus {
  following: boolean;
  targetId?: string;
  mode?: FollowMode;
  targetPosition?: { q: number; r: number };
  distance?: number;
}

// ============ MAP TERRAIN ============

export type TerrainType =
  | 'nebula' | 'asteroid_field' | 'black_hole' | 'wormhole' | 'ion_storm'
  | 'debris_field' | 'radiation_zone' | 'gas_cloud' | 'solar_flare'
  | 'space_rift' | 'gravity_well' | 'dark_sector' | 'mineral_vein'
  | 'derelict_ship' | 'ancient_beacon' | 'trade_route' | 'custom';

export interface MapTerrain {
  id: string;
  worldId: number;
  positionQ: number;
  positionR: number;
  terrainType: TerrainType;
  displayName: string;
  isInteractive: boolean;
  isObstacle: boolean;
  properties: Record<string, any>;
  visualConfig: Record<string, any>;
}

// ============ TELEGRAM ANNOUNCEMENTS ============

export type AnnouncementType = 'asteroid_spawn' | 'event' | 'maintenance' | 'update' | 'custom';
export type AnnouncementStatus = 'scheduled' | 'sent' | 'failed' | 'cancelled';

export interface TelegramAnnouncement {
  id: string;
  announcementType: AnnouncementType;
  title: string;
  message: string;
  metadata: Record<string, any>;
  scheduledAt: string;
  sentAt: string | null;
  status: AnnouncementStatus;
  createdAt: string;
}

// ============ TREASURY MANAGEMENT ============

export interface TreasuryPayment {
  id: string;
  purchaseId: string | null;
  userId: string;
  chain: Chain;
  token: Stablecoin;
  amountRaw: string;
  amountUsd: number;
  txHash: string;
  fromAddress: string;
  toAddress: string;
  blockNumber: number | null;
  confirmations: number;
  status: 'pending' | 'confirming' | 'verified' | 'failed';
  createdAt: string;
  verifiedAt: string | null;
}

export interface TreasuryLPPosition {
  id: string;
  chain: string;
  dexName: string;
  poolPair: string;
  poolAddress: string | null;
  amountDeposited: number;
  currentValue: number;
  unrealizedPnl: number;
  totalFeesEarned: number;
  allocatedToAsteroids: number;
  status: 'active' | 'closed' | 'withdrawn';
  notes: string;
  depositedAt: string;
  lastUpdatedAt: string;
  closedAt: string | null;
}

export interface TreasuryLPSnapshot {
  id: string;
  positionId: string;
  currentValue: number;
  feesEarned: number;
  impermanentLossPct: number;
  notes: string;
  recordedAt: string;
}

export interface TreasuryAllocation {
  id: string;
  sourcePositionId: string | null;
  destination: 'golden_asteroids' | 'operations' | 'development' | 'marketing';
  amount: number;
  notes: string;
  allocatedAt: string;
}

export interface TreasurySummary {
  totalDeposited: number;
  totalCurrentValue: number;
  totalUnrealizedPnl: number;
  totalFeesEarned: number;
  totalAllocated: number;
  positionCount: number;
  byChain: Record<string, {
    deposited: number;
    currentValue: number;
    pnl: number;
    positions: number;
  }>;
}

// ============ TOKEN LAUNCH ============

export type TokenLaunchOpType = 'generate_wallets' | 'fund_wallets' | 'buy_tokens' | 'consolidate' | 'check_progress';
export type TokenLaunchOpStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface TokenLaunchOperation {
  id: string;
  operationType: TokenLaunchOpType;
  tokenName: string;
  tokenMint: string | null;
  status: TokenLaunchOpStatus;
  progressPct: number;
  walletsTotal: number;
  walletsCompleted: number;
  walletsFailed: number;
  totalSolSpent: number;
  totalTokensAcquired: number;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

// ============ MINERAL REPAIR COST CONSTANTS ============

export const REPAIR_MINERAL_COSTS: Record<string, { mineralId: string; perHp: number; perShield: number; fixed: number }> = {
  iron:     { mineralId: 'min_iron',     perHp: 0.1, perShield: 0,    fixed: 0 },
  copper:   { mineralId: 'min_copper',   perHp: 0,   perShield: 0.05, fixed: 0 },
  rhodium:  { mineralId: 'min_rhodium',  perHp: 0,   perShield: 0,    fixed: 2 },
};

// ============ ORBITAL PLATFORM SYSTEM ============

export type PlatformTier = 'basic' | 'advanced' | 'elite' | 'legendary';
export type PlatformStatus = 'inventory' | 'active' | 'destroyed';

export interface OrbitalPlatform {
  id: string;
  userId: string | null;
  worldId: number;
  planetId: string;
  platformTier: PlatformTier;
  positionQ: number | null;
  positionR: number | null;
  currentHp: number;
  maxHp: number;
  currentShields: number;
  maxShields: number;
  mineralStorageCapacity: number;
  hasRepairBay: boolean;
  hasShipyard: boolean;
  hasUnitBuilding: boolean;
  hasOrbitalMining: boolean;
  isPublic: boolean;
  status: PlatformStatus;
  placedAt: string | null;
  createdAt: string;
}

export interface PlanetResource {
  planetId: string;
  mineralTypeId: string;
  totalDeposit: number;
  remainingDeposit: number;
  miningRatePerTick: number;
}

export const PLATFORM_USD_PRICES: Record<PlatformTier, number> = {
  basic: 500,
  advanced: 2000,
  elite: 10000,
  legendary: 50000,
};

export const PLATFORM_HP: Record<PlatformTier, number> = {
  basic: 50,
  advanced: 100,
  elite: 250,
  legendary: 500,
};

export const PLATFORM_SHIELDS: Record<PlatformTier, number> = {
  basic: 25,
  advanced: 50,
  elite: 150,
  legendary: 300,
};

export const PLATFORM_STORAGE: Record<PlatformTier, number> = {
  basic: 300,
  advanced: 1000,
  elite: 5000,
  legendary: 10000,
};

export const PLATFORM_SHIELD_REGEN: Record<PlatformTier, number> = {
  basic: 1,
  advanced: 2,
  elite: 3,
  legendary: 5,
};

export interface PlatformCapabilities {
  dock: boolean;
  repair: boolean;
  shipyard: boolean;
  build: boolean;
  mining: boolean;
}

export const PLATFORM_CAPABILITIES: Record<PlatformTier, PlatformCapabilities> = {
  basic:     { dock: true, repair: false, shipyard: false, build: false, mining: false },
  advanced:  { dock: true, repair: true,  shipyard: false, build: false, mining: false },
  elite:     { dock: true, repair: true,  shipyard: true,  build: true,  mining: false },
  legendary: { dock: true, repair: true,  shipyard: true,  build: true,  mining: true  },
};
